import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingSessionGpuView, TrainingSessionPodMetricsView } from "@/api/trainingEval";
import { hatcheryTextModes } from "@/app/theme";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  cpuUsageLabel,
  gpuUtilPercent,
  memoryUsageLabel,
  resourceFreshnessLabel,
  sessionPodLabel,
} from "./trainingSessionDetailFormat";
import styles from "./SessionResourceCard.module.css";
import panelStyles from "./TrainingSessionPrimaryPanels.module.css";
import { useNowMs } from "./useNowMs";
import { useSessionGpuMetrics } from "./useSessionGpuMetrics";

// "n초 전 갱신" 을 다시 계산하는 주기. 폴링 주기(15초)보다 짧아야 숫자가 움직인다.
const freshnessTickMs = 5_000;

/**
 * 진행 중인 세션의 pod·GPU 순간 지표 카드. 로그 탭이 세션이 진행 중일 때만 마운트한다.
 *
 * 값은 요청 시점의 순간값이다. 옆의 "실행 환경과 시간" 카드는 제출할 때 요청한 리소스를 표시하고,
 * 이 카드는 지금 쓰고 있는 양을 표시한다. Node 이름과 IP 는 응답에 없다.
 */
export function SessionResourceCard({ sessionId }: { sessionId: string }): ReactNode {
  const { data, fetchedAtMs, error, refetch } = useSessionGpuMetrics(sessionId);
  const nowMs = useNowMs(fetchedAtMs !== null, freshnessTickMs);

  return (
    <section className={panelStyles.card} aria-label="실행 중 리소스">
      <div className={panelStyles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>실행 중 리소스</Text>
        {fetchedAtMs === null ? null : (
          <span className={panelStyles.cardMeta}>
            {resourceFreshnessLabel(fetchedAtMs, nowMs, error !== null)}
          </span>
        )}
      </div>
      {data === null ? (
        error === null ? (
          <LoadingState size="sm">실행 중 리소스를 불러오고 있습니다.</LoadingState>
        ) : (
          <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: refetch }}>
            실행 중 리소스를 불러오지 못했습니다. {error}
          </EmptyState>
        )
      ) : data.pods.length === 0 ? (
        <EmptyState size="sm">실행 중인 pod가 없습니다. Training이 시작되면 표시됩니다.</EmptyState>
      ) : (
        <ul className={styles.pods}>
          {data.pods.map((pod) => (
            <PodMetrics key={pod.pod} pod={pod} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PodMetrics({ pod }: { pod: TrainingSessionPodMetricsView }): ReactNode {
  return (
    <li className={styles.pod}>
      <div className={styles.podRow}>
        <HatTextTooltip text={pod.pod}>
          <span className={styles.podName}>{sessionPodLabel(pod)}</span>
        </HatTextTooltip>
        <span className={styles.pair}>
          <span className={styles.pairLabel}>CPU</span>
          {cpuUsageLabel(pod.cpu_cores, pod.cpu_limit_cores)}
        </span>
        <span className={styles.pair}>
          <span className={styles.pairLabel}>메모리</span>
          {memoryUsageLabel(pod.mem_used_bytes, pod.mem_limit_bytes)}
        </span>
      </div>
      {pod.gpus.length === 0 ? null : (
        <ul className={styles.gpus}>
          {pod.gpus.map((gpu, index) => (
            // 상류는 GPU 순번을 주지 않는다. 그래서 응답 순서의 번호를 이름으로 쓴다.
            <GpuMetrics key={gpu.accelerator_id ?? index} gpu={gpu} index={index} />
          ))}
        </ul>
      )}
    </li>
  );
}

function GpuMetrics({ gpu, index }: { gpu: TrainingSessionGpuView; index: number }): ReactNode {
  const percent = gpuUtilPercent(gpu.util_pct);
  const name = `GPU ${index}`;
  return (
    <li className={styles.gpuRow}>
      <span className={styles.gpuHead}>
        {gpu.accelerator_id === null ? (
          <span className={styles.gpuName}>{name}</span>
        ) : (
          <HatTextTooltip text={gpu.accelerator_id}>
            <span className={styles.gpuName}>{name}</span>
          </HatTextTooltip>
        )}
        <span className={styles.gpuModel}>{gpu.model ?? "—"}</span>
      </span>
      <span className={styles.util}>
        {percent === null ? null : (
          <span
            className={styles.meterTrack}
            role="progressbar"
            aria-label={`${name} 사용률 ${percent}%`}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className={styles.meterFill} style={{ width: `${percent}%` }} />
          </span>
        )}
        {percent === null ? <span>—</span> : <span className={styles.utilValue}>{percent}%</span>}
      </span>
      <span className={styles.pair}>
        <span className={styles.pairLabel}>메모리</span>
        {memoryUsageLabel(gpu.mem_used_bytes, gpu.mem_total_bytes)}
      </span>
    </li>
  );
}
